import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { Chip, IconButton, Switch } from "@mui/material";
import React from "react";

import { TableCell, TableRow } from "@/components/molecules/Table/styled";
import { CONTACT_PERMISSIONS } from "@/constants/modules.permissions";
import SWR_KEYS from "@/constants/swr-keys";
import useContactData from "@/hooks/data/use-contact-data";
import usePopOver from "@/hooks/use-popover";
import useHasNoPermission from "@/hooks/use-check-permissions";
import useMutate from "@/hooks/use-mutate";
import useUpdateContact from "@/hooks/requests/contact/use-update-contact";
import IContact from "@/interfaces/contact.interface";
import { ContactsTableMessages } from "@/messages/contacts/contacts-table.messages";

import { ContactPopover } from "../ContactPopover";

interface Props {
  contact: IContact;
  index: number;
  messages: ContactsTableMessages;
}

export function ContactTableRow({ contact, index, messages }: Props) {
  const data = useContactData({ contact, messages });
  const { openPopover, anchorEl, closePopover, isOpen, selected } = usePopOver<IContact>();

  const { update } = useUpdateContact();
  const { mutate } = useMutate();
  const hasNoPermissionToEdit = useHasNoPermission(CONTACT_PERMISSIONS.EDIT);

  const isActive = contact.active !== false;

  const handleToggleActive = async (current: IContact) => {
    const _id = Number(current.id) || 0;
    const formData = { ...(current as any), active: !isActive } as any;

    await update({
      _id,
      formData,
      successMessage: "",
      errorMessage: "",
      onSuccess: () => mutate(SWR_KEYS.getContacts),
      onError: () => undefined,
    });
  };

  return (
    <TableRow index={index}>
      <TableCell
        sx={{
          verticalAlign: "top",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: 0,
        }}
        title={data.fullName}
      >
        {data.fullName}
      </TableCell>
      <TableCell
        sx={{
          verticalAlign: "top",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: 0,
        }}
        title={data.email || ""}
      >
        {data.email}
      </TableCell>
      <TableCell sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}>
        {data.phoneNumber}
      </TableCell>
      <TableCell
        sx={{
          verticalAlign: "top",
          maxWidth: 0,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={data.cityDisplay}
      >
        {data.cityDisplay}
      </TableCell>
      <TableCell sx={{ verticalAlign: "top" }} title={data.stateDisplay}>
        {data.stateDisplay}
      </TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>
        <Chip
          label={(data.consentStatusTranslated ? data.consentStatusTranslated : "-").toUpperCase()}
          color={data.consentBgColor}
          variant="outlined"
          sx={{
            maxWidth: "100%",
            height: "auto",
            "& .MuiChip-label": {
              whiteSpace: "normal",
              overflow: "visible",
              textAlign: "center",
              py: 0.5,
              fontSize: "inherit",
            },
          }}
        />
      </TableCell>
      <TableCell sx={{ verticalAlign: "top", textAlign: "center" }}>
        <Switch
          checked={isActive}
          disabled={hasNoPermissionToEdit}
          onClick={(e) => {
            e.stopPropagation();
            void handleToggleActive(contact);
          }}
        />
      </TableCell>
      <TableCell>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            openPopover(e, contact);
          }}
        >
          <MoreHorizIcon />
        </IconButton>

        {selected && (
          <ContactPopover
            isOpen={isOpen}
            anchorEl={anchorEl}
            onClose={closePopover}
            messages={messages}
            selected={selected}
          />
        )}
      </TableCell>
    </TableRow>
  );
}
